/**
 * Lecture des quotas d'entreprise — SOURCE DE VÉRITÉ UNIQUE côté front.
 *
 * L'arithmétique ici est triviale (`current >= limit`). Ce que ce fichier centralise,
 * c'est la CONNAISSANCE du contrat serveur, qui l'est beaucoup moins :
 *
 * ── 1. `max_stocks` est DÉJÀ la limite EFFECTIVE ──────────────────────────────
 * `Company.to_dict()` (backend/models/company.py) expose `max_stocks` résolu par
 * `effective_limits`, c'est-à-dire `override ?? valeur du plan`. Le front ne doit
 * JAMAIS le recalculer, ni aller chercher PLAN_LIMITS : la résolution appartient au
 * serveur, qui est aussi celui qui applique le cap.
 *
 * ⚠️ PIÈGE POUR UI-5 (formulaire d'édition) : `max_stocks` et `max_stocks_override`
 * sont DEUX champs distincts dans la même charge utile.
 *   - `max_stocks`          = limite effective, LECTURE SEULE, jamais renvoyée au serveur
 *   - `max_stocks_override` = l'override brut, `null` si l'entreprise suit son plan
 * Un formulaire qui pré-remplirait avec `max_stocks` puis renverrait cette valeur
 * transformerait « suit le plan » en « override figé à la valeur du plan » — sans
 * message d'erreur, et sans que rien ne bouge à l'écran. Idem pour `users_per_stock`.
 *
 * ── 2. DEUX FORMES DE RÉPONSE, non interchangeables ───────────────────────────
 * Les VALEURS viennent de requêtes SQL identiques des deux côtés, mais la
 * SÉRIALISATION diffère :
 *   GET /api/admin/company    -> { company: {...}, usage: { nb_stocks, nb_users } }
 *   GET /api/admin/companies  -> { companies: [ { ...company, nb_stocks, nb_users, nb_admins } ] }
 * (`nb_admins` n'existe que côté pluriel : il vient de _usage(), que la route singulière
 * ne partage pas — elle recompte en ligne. Divergence de plus entre les deux contrats.)
 * D'où les deux adaptateurs ci-dessous. C'est le seul endroit du front qui connaît
 * cette asymétrie.
 *
 * ── 3. `nb_users` ET `nb_admins` NE SONT PAS DES QUOTAS ───────────────────────
 * Aucune fonction de ce fichier ne les traite comme tels, volontairement. Le cap
 * `users_per_stock` s'applique PAR STOCK et ne compte que les `role == 'USER'`
 * (cf. `count_stock_users` côté backend), alors que `nb_users` compte TOUS les
 * utilisateurs de l'entreprise, ADMIN inclus. Les afficher en « x / y » serait faux.
 * `nb_admins` est un compteur d'anomalie (0 = entreprise inutilisable, personne ne peut
 * y gérer stocks ni comptes) : il ne se rapporte à aucune limite non plus.
 * Ce sont des métriques d'information, pas des rapports à une limite.
 *
 * Ce fichier ne rend RIEN et ne porte AUCUNE chaîne i18n : le bandeau d'Admin.js
 * (une phrase dans un <Alert>) et la cellule de la console super-admin (un « x / y »
 * dont l'en-tête de colonne porte déjà le mot) n'ont ni la même forme ni les mêmes
 * textes. On partage la logique, pas la présentation.
 */

/**
 * Plans sélectionnables dans le formulaire de la console super-admin.
 *
 * Duplique volontairement les CLÉS de PLAN_LIMITS (backend/constants/plans.py). Aucun
 * endpoint n'expose la liste : elle ne transparaît que dans le corps d'une 400
 * (`valid_plans`). Le SERVEUR RESTE L'AUTORITÉ — POST et PUT rejettent tout plan hors
 * PLAN_LIMITS avec « Plan invalide » + la liste valide.
 *
 * Duplication assumée parce que la dérive est BRUYANTE : un plan retiré ou renommé côté
 * serveur produit un 400 explicite au premier envoi, pas un échec muet. À l'inverse, on
 * ne duplique PAS les VALEURS des limites (max_stocks, users_per_stock) — une dérive y
 * serait SILENCIEUSE : le formulaire afficherait une limite héritée fausse sans qu'aucune
 * erreur ne survienne. La valeur héritée se lit dans la réponse serveur (`max_stocks`
 * quand `max_stocks_override` est null), elle ne se recalcule jamais ici.
 *
 * `ultra` est absent, comme côté serveur : plans.py le décrit en commentaire comme un
 * plan « sur devis » mais la validation le REFUSE (il n'a pas d'entrée dans PLAN_LIMITS).
 * Cette contradiction est une décision produit à trancher, notée au ROADMAP.
 */
export const SELECTABLE_PLANS = ['basic', 'pro'];

/**
 * Cœur du calcul. Retourne `null` si l'une des deux valeurs n'est pas exploitable —
 * l'appelant décide alors de masquer (bandeau décoratif) ou d'afficher une erreur.
 *
 * ⚠️ Test `typeof` sur LES DEUX opérandes, À NE PAS SIMPLIFIER. En JS, `2 >= null`
 * vaut `true` par coercition de `null` vers 0 : sans cette garde, un quota dont la
 * limite est `null` serait rendu « atteint » à tort. Ce n'est pas théorique — un plan
 * à stocks illimités se modéliserait naturellement par `null`, et le plan `ultra` est
 * déjà noté au ROADMAP comme mal résolu côté serveur (`get_plan_limits` dégrade
 * silencieusement sur `basic` pour tout plan inconnu).
 * `NaN` n'est PAS gardé, volontairement : `typeof NaN === 'number'` le laisse passer,
 * mais il est inatteignable ici — les valeurs viennent d'un `COUNT(*)` sérialisé en
 * JSON, et JSON n'a pas de littéral `NaN`, donc `JSON.parse` ne peut pas en produire.
 */
export const buildStockQuota = (current, limit) => {
  if (typeof current !== 'number' || typeof limit !== 'number') {
    return null;
  }
  return { current, limit, reached: current >= limit };
};

/** Adaptateur pour GET /api/admin/company — usage IMBRIQUÉ (bandeau ADMIN, UI-3). */
export const stockQuotaFromMyCompany = (payload) => {
  if (!payload?.company || !payload?.usage) {
    return null;
  }
  return buildStockQuota(payload.usage.nb_stocks, payload.company.max_stocks);
};

/** Adaptateur pour GET /api/admin/companies — usage APLATI (console SA, UI-4). */
export const stockQuotaFromCompanyRow = (row) => {
  if (!row) {
    return null;
  }
  return buildStockQuota(row.nb_stocks, row.max_stocks);
};
